/**
 * Before/after viewer like the desktop app's `CompareView`: Cạnh nhau · Kéo so sánh · Chỉ kết quả · Chỉ gốc.
 * "Gốc" is the photo as chosen in this browser (the server keeps no copy to show back); "Kết quả" is the job's JPEG.
 * An advanced-edit (AI) job also has the photo processed without AI: three panes Gốc | Xử lý thường | Kết quả AI, and
 * the slider compares the last two.
 */
import { useId, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import type { Job } from "../api";
import { AI_RESULT, COMPARE_MODE_LABELS, STATUS_LABELS, rejectMessage } from "../messages";
import { readLocal, writeLocal } from "../storage";
import { QueueWait } from "./QueueWait";

type Mode = "side" | "slider" | "result" | "source";
const MODES: Mode[] = ["side", "slider", "result", "source"];
const MODE_KEY = "idphoto.compare.v1";

interface Props {
  source: string | null;
  /** Caption of the "Gốc" pane: the full photo, or the render machine's crop that lines up with the result. */
  sourceTitle?: string;
  /** What the "Gốc" pane says when there is no local copy of the photo. */
  sourceMissing: string;
  /** width / height of the photo spec: on narrow screens the side-by-side frames take this shape, so the result fills
      their height instead of floating in a frame shaped by the screen. */
  ratio?: number;
  job: Job | null;
  resultUrl: string | null;
  /** AI jobs: the same photo processed without AI (`job.files.local`) */
  localUrl?: string | null;
}

function Progress({ job }: { job: Job }) {
  const frac = job.status === "queued" ? 0 : Math.max(0.05, job.progress?.frac ?? 0.05);
  return (
    <div className="progress-wrap">
      <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100}
           aria-valuenow={Math.round(frac * 100)} aria-label={STATUS_LABELS[job.status]}>
        <div className="progress-bar" style={{ width: `${Math.round(frac * 100)}%` }} />
      </div>
      <p aria-live="polite">
        {STATUS_LABELS[job.status]}
        {job.status === "running" && job.progress?.stage ? `: ${job.progress.stage}` : "…"}
      </p>
      {job.status === "queued" && <QueueWait position={job.queue_position} eta={job.eta_s} className="" />}
    </div>
  );
}

function ResultBody({ job, resultUrl }: { job: Job | null; resultUrl: string | null }) {
  if (!job) return <p className="pane-empty">Kết quả hiện ở đây sau khi render</p>;
  if (job.status === "queued" || job.status === "running") return <div className="pane-empty"><Progress job={job} /></div>;
  if (job.status === "done" && resultUrl) return <img src={resultUrl} alt="Ảnh thẻ đã tạo" />;
  if (job.status === "done") return <p className="pane-empty">Ảnh đã bị xoá khỏi máy chủ.</p>;
  return (
    <div role="alert" className="pane-empty pane-error">
      <strong>{STATUS_LABELS[job.status]}.</strong> {rejectMessage(job.reject_reason)}
    </div>
  );
}

export function CompareView({ source, sourceTitle = "Ảnh gốc", sourceMissing, job, resultUrl, localUrl, ratio = 2 / 3 }: Props) {
  const groupName = useId();
  const [mode, setMode] = useState<Mode>(() => {
    const saved = readLocal(MODE_KEY);
    return MODES.includes(saved as Mode) ? (saved as Mode) : "side";
  });
  const [pos, setPos] = useState(50);
  const stage = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const showResult = job?.status === "done" && resultUrl;
  const local = showResult && localUrl ? localUrl : null;
  const base = local ?? source;                       // what the slider compares the result with
  const sliderReady = Boolean(base && showResult);
  const shown: Mode = mode === "slider" && !sliderReady ? "side" : mode;

  function pick(m: Mode) {
    setMode(m);
    writeLocal(MODE_KEY, m);
  }

  function follow(e: PointerEvent<HTMLDivElement>) {
    if (!dragging.current || !stage.current) return;
    const box = stage.current.getBoundingClientRect();
    if (box.width > 0) setPos(Math.min(100, Math.max(0, ((e.clientX - box.left) / box.width) * 100)));
  }

  const sourcePane = (
    <figure className="pane">
      <figcaption>{sourceTitle}</figcaption>
      <div className="pane-view">
        {source ? <img src={source} alt="Ảnh gốc" /> : <p className="pane-empty">{sourceMissing}</p>}
      </div>
    </figure>
  );
  const localPane = local && (
    <figure className="pane">
      <figcaption>{AI_RESULT.local}</figcaption>
      <div className="pane-view"><img src={local} alt="Ảnh thẻ xử lý thường, không qua AI" /></div>
    </figure>
  );
  const resultPane = (
    <figure className="pane">
      <figcaption>{local ? AI_RESULT.ai : "Kết quả"}</figcaption>
      <div className="pane-view"><ResultBody job={job} resultUrl={resultUrl} /></div>
    </figure>
  );

  return (
    <section className="viewer card" aria-labelledby={`${groupName}-title`}>
      <div className="viewer-bar">
        <h2 id={`${groupName}-title`} className="visually-hidden">So sánh ảnh gốc và kết quả</h2>
        <div className="segmented" role="radiogroup" aria-label="Chế độ xem">
          {MODES.map((m) => (
            <label key={m} className={`segment${mode === m ? " is-selected" : ""}`}>
              <input type="radio" name={groupName} value={m} checked={mode === m} onChange={() => pick(m)} />
              {COMPARE_MODE_LABELS[m]}
            </label>
          ))}
        </div>
        {mode === "slider" && !sliderReady && <small className="hint">Kéo so sánh dùng được khi đã có ảnh gốc và kết quả.</small>}
      </div>

      {shown === "side" && (
        <div className={`panes ${local ? "panes-3" : "panes-2"}`} style={{ "--pane-ratio": String(ratio) } as CSSProperties}>
          {sourcePane}{localPane}{resultPane}
        </div>
      )}
      {shown === "result" && <div className="panes">{resultPane}</div>}
      {shown === "source" && <div className="panes">{sourcePane}</div>}
      {shown === "slider" && base && resultUrl && (
        <div className="panes">
          <figure className="pane">
            <figcaption>{local ? AI_RESULT.slider : "Ảnh gốc ◂ ▸ Kết quả"}</figcaption>
            <div ref={stage} className="pane-view slider-stage"
                 onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); follow(e); }}
                 onPointerMove={follow}
                 onPointerUp={() => { dragging.current = false; }}
                 onPointerCancel={() => { dragging.current = false; }}>
              <img src={base} alt={local ? "Ảnh thẻ xử lý thường, không qua AI" : "Ảnh gốc"} draggable={false} />
              <img src={resultUrl} alt="Ảnh thẻ đã tạo" draggable={false} className="slider-top"
                   style={{ clipPath: `inset(0 0 0 ${pos}%)` }} />
              <span className="slider-line" style={{ left: `${pos}%` }} aria-hidden="true" />
            </div>
          </figure>
          <input type="range" className="slider-range" min={0} max={100} value={Math.round(pos)}
                 aria-label="Vị trí đường so sánh" onChange={(e) => setPos(Number(e.target.value))} />
        </div>
      )}
    </section>
  );
}
